/**
 * Ecosystem-wide auto-recovery from stale dynamic-import failures.
 *
 * Every app is a Vite SPA whose pages are lazy-loaded via `import()`. Vite
 * code-splits each page into a content-hashed chunk (e.g.
 * `notifications-bYV9C4s6.js`). After a redeploy the hashes change and the old
 * files are removed from the server. A browser tab that still holds the
 * PREVIOUS `index.html` keeps the old module map in memory, so the next lazy
 * navigation requests a chunk URL that no longer exists and throws
 * "Failed to fetch dynamically imported module". A full reload pulls the fresh
 * `index.html` (and its new chunk names) and fixes it — which is exactly why
 * the error "rectifies itself on reload".
 *
 * This installs a one-shot auto-reload so the user never sees the error: when a
 * dynamic import fails because the chunk is stale, we reload the page for them.
 * A short-lived sessionStorage guard prevents a reload LOOP — if the chunk is
 * genuinely missing (a broken build rather than a stale tab) the reload won't
 * fix it, so after one attempt we let the error surface to the router's own
 * "Something went wrong" handler instead of thrashing.
 *
 * Two triggers, for belt-and-braces coverage:
 *   1. Vite's own `vite:preloadError` event (fired by the __vitePreload helper
 *      that wraps lazy imports) — the primary, precise signal.
 *   2. `unhandledrejection` whose message matches a stale-chunk pattern — a
 *      fallback for any import the preload helper didn't wrap, and for the
 *      cross-browser wording variants.
 */

// Reload at most once per this window. Long enough that a stale tab recovers in
// a single reload; short enough that a genuinely-broken build surfaces its error
// rather than reloading forever.
const RELOAD_GUARD_MS = 10_000;
const GUARD_KEY = 'isg:stale-chunk-reloaded-at';

// Substrings that identify a failed dynamic-import across browsers. Chromium,
// Firefox and Safari each word it differently; Vite's CSS preload helper adds
// its own. Kept broad on purpose — a false positive only costs one reload.
// These are the BROWSERS' own error texts, matched — never shown to anyone —
// so they must stay English whatever the reader's language. The module has
// nothing user-visible: a stale tab reloads silently.
const STALE_PATTERNS = [
  'Failed to fetch dynamically imported module',  // Chromium  i18n-ignore: browser error text we match, not UI
  'error loading dynamically imported module',     // Firefox   i18n-ignore: browser error text we match, not UI
  'Importing a module script failed',              // Safari    i18n-ignore: browser error text we match, not UI
  'Unable to preload CSS',                         // Vite      i18n-ignore: Vite error text we match, not UI
];

let _installed = false;

function looksStale(message) {
  if (typeof message !== 'string') return false;
  return STALE_PATTERNS.some((p) => message.includes(p));
}

/**
 * Reload once to pick up the fresh index.html + chunk names. Returns true if a
 * reload was triggered, false if suppressed by the loop guard (so callers can
 * decide whether to let the original error propagate).
 */
function recover() {
  let last = 0;
  try { last = Number(sessionStorage.getItem(GUARD_KEY)) || 0; } catch { /* sessionStorage unavailable */ }
  const now = Date.now();
  if (now - last < RELOAD_GUARD_MS) return false;  // already tried — don't loop
  try { sessionStorage.setItem(GUARD_KEY, String(now)); } catch { /* ignore */ }
  // location.reload() defaults to a normal (HTTP-cache-aware) reload, which is
  // what we want: the new index.html is what fixes the stale module map.
  window.location.reload();
  return true;
}

/**
 * Install the global stale-chunk auto-reload. Idempotent and safe to call from
 * multiple entry points; only the first call registers the listeners.
 */
export function initStaleChunkReload() {
  if (_installed || typeof window === 'undefined') return;
  _installed = true;

  // Primary: Vite fires this on the window when a lazy import's preload fails.
  window.addEventListener('vite:preloadError', (e) => {
    // preventDefault() stops Vite from re-throwing, so the router's catch
    // doesn't flash "Something went wrong" before the reload kicks in.
    if (recover()) e.preventDefault();
  });

  // Fallback: catch stale-chunk rejections the preload event didn't cover.
  window.addEventListener('unhandledrejection', (e) => {
    const msg = e && e.reason && e.reason.message;
    if (looksStale(msg) && recover()) e.preventDefault();
  });
}
